import React, { useEffect, useRef, useState } from "react";
import { Radio, RadioGroup, toaster, Toggle, Whisper } from "rsuite";
import SynchronizationNtpControl from "./SynchronizationNtpControl";
import SynchronizationGpsControl from "./SynchronizationGpsControl";
import { getTooltip } from "../../../../../shared/Tooltip";
import NotificationFactory from "../../../../../shared/Notifications";
import { DevicesTimeSynchronizationSource, TimeSynchronizationSource } from "../../../../../api";
import { SharedApi } from "../../../../../services/Shared.api";
import UpdateButton from "../../../../shared/UpdateButton/UpdateButton";
import {SynchronizationApi} from "../../../../../services/Synchronization.api";
import {RefreshRef, SynchronizationControlProps} from "./Synchronization.model";
import ReactYandexMap from "../../../../shared/ReactYandexMap/ReactYandexMap";

const SynchronizationControl = ({ clockStationId, executeCommand, isClockStation }: SynchronizationControlProps) => {
  const [loading, setLoading] = useState(false);
  const [ntpSyncEnabled, setNtpSyncEnabled] = useState(false);
  const [gpsSyncEnabled, setGpsSyncEnabled] = useState(false);
  const [syncSource, setSyncSource] = useState<DevicesTimeSynchronizationSource | null>(null);
  const [currentGpsCoordinates, setCurrentGpsCoordinates] = useState<string | null>(null);
  const [storedGpsCoordinates, setStoredGpsCoordinates] = useState<string | null>(null);
  const ntpRefreshRef = useRef<(() => Promise<void> | void) | null>(null);

  useEffect(() => {
    readSyncState().then();
    getSyncSource().then();
  }, [clockStationId]);

  const changeGpsState = async (state: boolean) => {
    setLoading(true);

    try {
      const result = await SynchronizationApi.updateGpsState(clockStationId, state)

      if (result) {
        setGpsSyncEnabled(state);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const changeNtpState = async (state: boolean) => {
    setLoading(true);
    try {
      const result = await SynchronizationApi.updateGpsNtpState(clockStationId, state);

      if (result) {
        setNtpSyncEnabled(state);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const readSyncState = async () => {
    setLoading(true);
    try {
      const response = await SynchronizationApi.getSynchronization(clockStationId)
      if (response) {

        setNtpSyncEnabled(Boolean(response.ntpSyncEnabled));
        setGpsSyncEnabled(Boolean(response.gpsSyncEnabled));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeStationSync = () => {
    synchronizeTime("ClockStation").then();
  };

  const handleNTPServerSync = () => {
    synchronizeTime("Ntp").then();
  };

  const synchronizeTime = async (synchronizationSource: TimeSynchronizationSource) => {
    setLoading(true);

    try {
      const result = await SynchronizationApi.synchronizeTime(clockStationId, synchronizationSource );

      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });
      } else {
        toaster.push(NotificationFactory.info("Синхронизации отравлена"), {
          placement: "topEnd",
          duration: 3000
        });
      }
    } catch (error) {
      console.error("Ошибка синхронизации", error);
      toaster.push(NotificationFactory.info("Синхронизации отравлена"), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const getSyncSource = async () => {
    try {
      const result = await SharedApi.getSyncSource(clockStationId as string);

      if (result) {
        setSyncSource(result.syncSource ?? null);
      }
    } catch (error) {
      console.error("Ошибка при получении:", error);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSourceChange = (mode: string) => {
    void (async () => {
    setLoading(true);
    try {
      const result = await SharedApi.updateSyncSource(clockStationId as string, mode as DevicesTimeSynchronizationSource);
      if (result) {
        setSyncSource(mode as DevicesTimeSynchronizationSource);
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });
      }
    } catch (error) {
      console.error("Ошибка при обновлении режима:", error);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
    })();
  };

  const handleTimeSyncNow = async () => {
    setLoading(true);
    try {
      const result = await SharedApi.timeSyncNow(clockStationId as string);

      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });

        getSyncSource().then();
      }
    } catch (error) {
      console.error("Ошибка при обновлении режима:", error);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await readSyncState();
    if (ntpRefreshRef.current) {
      await ntpRefreshRef.current();
    }
  };

  return (
    <>
      <div className="fieldset-wrapper mb-4">
        <div className="legend-title main-legend-title">Синхронизация</div>
        <section className="primary-clock p-2">
          <div className="row">
            <div className="col-md-12 text-end">
              <UpdateButton onRefresh={handleRefresh} loading={loading} />
            </div>
          </div>
          <div className="row p-2">
            {isClockStation && (
              <div className="col-md-5">
                <h6 className="mb-2">Приемник GPS/ГЛОНАСС</h6>
                <div>
                  <Toggle
                    color="blue"
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    loading={loading}
                    checked={gpsSyncEnabled}
                    onChange={changeGpsState}
                  >
                    <span>
                      Синхронизация по GPS/ГЛОНАСС {gpsSyncEnabled ? "включена" : "отключена"}
                    </span>
                  </Toggle>
                </div>
              </div>
            )}
            <div className="col-md-7">
              <h6 className="mb-2">Сервер времени</h6>
              <Toggle
                color="blue"
                checkedChildren="ON"
                unCheckedChildren="OFF"
                loading={loading}
                checked={ntpSyncEnabled}
                onChange={changeNtpState}
              >
                <span>Синхронизация по NTP {ntpSyncEnabled ? "включена" : "отключена"}</span>
              </Toggle>
            </div>
          </div>
          <div className="row p-2">
            <div className="col-md-5">
              <div className="col">
                {gpsSyncEnabled && (
                  <SynchronizationGpsControl
                    clockStationId={clockStationId}
                    executeCommand={executeCommand as any}
                    onCurrentCoordinatesChange={setCurrentGpsCoordinates}
                    onStoredCoordinatesChange={setStoredGpsCoordinates}
                  />
                )}
              </div>
            </div>
            <div className="col-md-7">
              <div className="col">
                {ntpSyncEnabled && (
                  <SynchronizationNtpControl
                    clockStationId={clockStationId}
                    onRefreshRef={ntpRefreshRef as RefreshRef}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="row text-center align-items-center ">
            {isClockStation && (
              <div className="col-md-2 mt-2">
                <Whisper
                  placement="top"
                  controlId="control-id-hover"
                  trigger="hover"
                  speaker={getTooltip("Синхронизация времени первичных часов от БКС")}
                >
                  <button
                    className="btn btn-outline-primary w-100"
                    type="button"
                    onClick={handleTimeStationSync}
                    disabled={loading}
                  >
                    {loading ? "Синхронизация..." : "Часовая станция"}
                  </button>
                </Whisper>
              </div>
            )}

            <div className="col-md-2 mt-2">
              <Whisper
                placement="top"
                controlId="control-id-hover"
                trigger="hover"
                speaker={getTooltip("Синхронизация времени БКС от NTP сервера")}
              >
                <button
                  className="btn btn-outline-primary w-100"
                  type="button"
                  onClick={handleNTPServerSync}
                  disabled={loading}
                >
                  {loading ? "Синхронизация..." : "NTP Сервер"}
                </button>
              </Whisper>
            </div>

            <div className="col-md-2 text-center d-flex justify-content-center">
              <RadioGroup
                disabled={loading}
                name="radio-group-inline"
                inline
                value={syncSource ?? undefined}
                onChange={(value) => handleSourceChange(String(value))}
              >
                <Radio color="blue" value="Esp">
                  <span className="radio-title">ESP</span>
                </Radio>
                {isClockStation && (
                  <Radio color="blue" value="ClockStation">
                    <span className="radio-title">ЧС</span>
                  </Radio>
                )}
              </RadioGroup>
            </div>
            <div className="col-md-3">
              <button
                className="btn btn-primary w-100"
                type="button"
                onClick={handleTimeSyncNow}
                disabled={loading}
              >
                {loading ? "Синхронизация..." : "Синхронизировать"}
              </button>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-md-6">
              <div className="mb-2 fw-semibold">Текущие координаты</div>
              <ReactYandexMap coordinates={currentGpsCoordinates ?? undefined} />
            </div>
            <div className="col-md-6">
              <div className="mb-2 fw-semibold">Сохранённые координаты</div>
              <ReactYandexMap coordinates={storedGpsCoordinates ?? undefined} />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SynchronizationControl;
